/**
 * Cover 6 Coverage Rules
 * Left side plays Cover 4 Match: CB1 deep quarter, FS deep quarter, DB/LB1 curl/flat
 * Right side plays Cover 2: SS deep half, CB2 flat, DB/LB2 curl/flat
 * LB plays hook zone in the middle
 * Requirements: 7.1, 7.3
 */

import { CoverageDefinition, CoverageReaction, Position, DBLBReaction, FSReaction, CB2Reaction, DBLB2Reaction, SSReaction, LBReaction } from '../types';

/* ── Starting Positions ── */

/** CB1 - Cover 4 side, press alignment on WR1 */
const CB1_COVER6_START: Position = { x: 20, y: 75 };

/** DB/LB1 - Cover 4 side, curl/flat zone */
const DBLB1_COVER6_START: Position = { x: 30, y: 72 };

/** FS - Cover 4 side, deep quarter (left-center) */
const FS_COVER6_START: Position = { x: 35, y: 45 };

/** CB2 - Cover 2 side, flat zone (press on WR4) */
const CB2_COVER6_START: Position = { x: 80, y: 75 };

/** DB/LB2 - Cover 2 side, curl/flat zone */
const DBLB2_COVER6_START: Position = { x: 70, y: 72 };

/** SS - Cover 2 side, deep half safety */
const SS_COVER6_START: Position = { x: 75, y: 40 };

/** LB - Hook zone, middle of field */
const LB_COVER6_START: Position = { x: 50, y: 70 };

/* ── CB1 Reactions (Cover 4 Match style) ──
 * Matches WR1 on deep routes (10+ yds), zone drops on short routes
 */
const cover6Reactions: CoverageReaction[] = [
  {
    routeTrigger: 'slant',
    wrPositionTrigger: { x: 20, y: 75 },
    cbResponse: [
      { x: 20, y: 55 },
      { x: 20, y: 35 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'out',
    wrPositionTrigger: { x: 20, y: 70 },
    cbResponse: [
      { x: 18, y: 55 },
      { x: 15, y: 35 },
    ],
    responseDelay: 0,
  },
  {
    // Palms "Out" call: WR2 (#2) runs short out in first 5 yards — CB1 covers WR2
    routeTrigger: 'out_wr2',
    wrPositionTrigger: { x: 30, y: 80 },
    cbResponse: [
      { x: 22, y: 72 },
      { x: 20, y: 70 },
      { x: 15, y: 70 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'deepOut',
    wrPositionTrigger: { x: 20, y: 45 },
    cbResponse: [
      { x: 20, y: 50 },
      { x: 7, y: 42 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'deepIn',
    wrPositionTrigger: { x: 20, y: 45 },
    cbResponse: [
      { x: 20, y: 50 },
      { x: 55, y: 42 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'comeback',
    wrPositionTrigger: { x: 20, y: 50 },
    cbResponse: [
      { x: 20, y: 35 },
      { x: 17, y: 42 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'go',
    wrPositionTrigger: { x: 20, y: 70 },
    cbResponse: [
      { x: 20, y: 40 },
      { x: 20, y: 8 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'post',
    wrPositionTrigger: { x: 20, y: 55 },
    cbResponse: [
      { x: 22, y: 50 },
      { x: 45, y: 18 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'corner',
    wrPositionTrigger: { x: 20, y: 55 },
    cbResponse: [
      { x: 22, y: 50 },
      { x: 7, y: 18 },
    ],
    responseDelay: 0,
  },
  {
    // Drag: Shallow cross - under 10yds, zone drop
    routeTrigger: 'drag',
    wrPositionTrigger: { x: 20, y: 80 },
    cbResponse: [
      { x: 20, y: 55 },
      { x: 20, y: 35 },
    ],
    responseDelay: 0,
  },
  {
    // Hitch: 5-yard stop - under 10yds, zone drop
    routeTrigger: 'hitch',
    wrPositionTrigger: { x: 20, y: 70 },
    cbResponse: [
      { x: 18, y: 55 },
      { x: 15, y: 35 },
    ],
    responseDelay: 0,
  },
  {
    // Curl: 10-yard stop - match WR1, close on turn
    routeTrigger: 'curl',
    wrPositionTrigger: { x: 20, y: 55 },
    cbResponse: [
      { x: 20, y: 50 },
      { x: 22, y: 58 },
    ],
    responseDelay: 0,
  },
];

/* ── DB/LB1 Reactions (Cover 4 Match style) ──
 * Curl/flat zone on left side. Covers WR2 short out, otherwise zones.
 */
const cover6DBLB1Reactions: DBLBReaction[] = [
  {
    routeTrigger: 'slant',
    response: [
      { x: 29, y: 65 },
      { x: 28, y: 55 },
      { x: 26, y: 45 },
    ],
  },
  {
    routeTrigger: 'out',
    response: [
      { x: 29, y: 65 },
      { x: 25, y: 70 },
      { x: 10, y: 70 },
    ],
  },
  {
    routeTrigger: 'out_wr1',
    response: [
      { x: 28, y: 65 },
      { x: 22, y: 68 },
      { x: 8, y: 68 },
    ],
  },
  {
    // Hitch by WR1: apex immediately breaks to cover WR1 on the observed hitch.
    routeTrigger: 'hitch_wr1',
    response: [
      { x: 28, y: 65 },
      { x: 24, y: 68 },
      { x: 22, y: 73 },
    ],
  },
  {
    // WR1 short in-breaker with no one in the flat — reroute #2, then cover WR1 across the field.
    // WR1 slant: (20,85) → (20,75) → (75,40)
    routeTrigger: 'slant_wr1',
    response: [
      { x: 30, y: 70 },  // brief reroute on #2
      { x: 35, y: 62 },  // peel off, work toward WR1's slant path
      { x: 50, y: 50 },
      { x: 65, y: 42 },
      { x: 75, y: 40 },  // arrive on WR1
    ],
  },
  {
    // WR1 short in-breaker with no one in the flat — reroute #2, then cover WR1 across the field.
    // WR1 drag: (20,85) → (20,80) → (70,75)
    routeTrigger: 'drag_wr1',
    response: [
      { x: 30, y: 70 },  // brief reroute on #2
      { x: 35, y: 72 },  // peel off, trail the shallow cross
      { x: 50, y: 73 },
      { x: 68, y: 74 },  // arrive on WR1
    ],
  },
  {
    routeTrigger: 'deepOut',
    response: [{ x: 29, y: 65 }, { x: 28, y: 55 }, { x: 26, y: 45 }],
  },
  {
    routeTrigger: 'deepIn',
    response: [{ x: 29, y: 65 }, { x: 28, y: 55 }, { x: 26, y: 45 }],
  },
  {
    routeTrigger: 'comeback',
    response: [{ x: 29, y: 65 }, { x: 28, y: 55 }, { x: 26, y: 45 }],
  },
  {
    routeTrigger: 'go',
    response: [{ x: 29, y: 65 }, { x: 28, y: 55 }, { x: 26, y: 45 }],
  },
  {
    routeTrigger: 'post',
    response: [{ x: 29, y: 65 }, { x: 28, y: 55 }, { x: 26, y: 45 }],
  },
  {
    routeTrigger: 'corner',
    response: [{ x: 29, y: 65 }, { x: 28, y: 55 }, { x: 26, y: 45 }],
  },
  {
    // Drag: Shallow cross - trail WR2 across
    routeTrigger: 'drag',
    response: [{ x: 29, y: 65 }, { x: 35, y: 60 }, { x: 60, y: 55 }],
  },
  {
    // Hitch: 5-yard stop - follow to flat
    routeTrigger: 'hitch',
    response: [{ x: 29, y: 65 }, { x: 25, y: 70 }, { x: 22, y: 73 }],
  },
  {
    // Curl: 10-yard stop - zone drop
    routeTrigger: 'curl',
    response: [{ x: 29, y: 65 }, { x: 28, y: 55 }, { x: 26, y: 45 }],
  },
];

/* ── FS Reactions (Cover 4 Match style) ──
 * Deep quarter on left-center. Matches WR2 on deep routes, zones on short.
 */
const cover6FSReactions: FSReaction[] = [
  {
    routeTrigger: 'slant',
    response: [{ x: 35, y: 30 }, { x: 35, y: 20 }],
  },
  {
    routeTrigger: 'out',
    response: [{ x: 35, y: 30 }, { x: 35, y: 20 }],
  },
  {
    routeTrigger: 'deepOut',
    response: [{ x: 35, y: 30 }, { x: 20, y: 35 }],
  },
  {
    routeTrigger: 'deepIn',
    response: [{ x: 35, y: 32 }, { x: 50, y: 35 }],
  },
  {
    routeTrigger: 'comeback',
    response: [{ x: 30, y: 28 }, { x: 25, y: 38 }],
  },
  {
    routeTrigger: 'go',
    response: [{ x: 35, y: 25 }, { x: 30, y: 8 }],
  },
  {
    routeTrigger: 'post',
    response: [{ x: 35, y: 35 }, { x: 45, y: 25 }, { x: 55, y: 15 }],
  },
  {
    routeTrigger: 'corner',
    response: [{ x: 32, y: 30 }, { x: 15, y: 12 }],
  },
  {
    routeTrigger: 'drag',
    response: [{ x: 35, y: 30 }, { x: 35, y: 20 }],
  },
  {
    routeTrigger: 'hitch',
    response: [{ x: 35, y: 30 }, { x: 35, y: 20 }],
  },
  {
    routeTrigger: 'curl',
    response: [{ x: 32, y: 28 }, { x: 28, y: 38 }],
  },
];

/* ── CB2 Reactions (Cover 2 flat style) ──
 * First looks for short out-breaking routes (WR3/WR4 out).
 * If none, matches WR4 on any route.
 */
const cover6CB2Reactions: CB2Reaction[] = [
  // Short out triggers - CB2 jumps the out route
  {
    routeTrigger: 'out_wr3',
    wrPositionTrigger: { x: 70, y: 80 },
    cbResponse: [
      { x: 78, y: 72 },
      { x: 80, y: 70 },
      { x: 85, y: 70 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'out_wr4',
    wrPositionTrigger: { x: 80, y: 78 },
    cbResponse: [
      { x: 82, y: 68 },
      { x: 85, y: 65 },
      { x: 92, y: 65 },
    ],
    responseDelay: 0,
  },
  // Match WR4 on all routes when no short out
  {
    routeTrigger: 'slant',
    wrPositionTrigger: { x: 80, y: 75 },
    cbResponse: [
      { x: 78, y: 65 },
      { x: 42, y: 42 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'deepOut',
    wrPositionTrigger: { x: 80, y: 45 },
    cbResponse: [
      { x: 80, y: 50 },
      { x: 93, y: 42 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'deepIn',
    wrPositionTrigger: { x: 80, y: 45 },
    cbResponse: [
      { x: 80, y: 50 },
      { x: 42, y: 42 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'comeback',
    wrPositionTrigger: { x: 80, y: 50 },
    cbResponse: [
      { x: 80, y: 35 },
      { x: 83, y: 42 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'go',
    wrPositionTrigger: { x: 80, y: 70 },
    cbResponse: [
      { x: 80, y: 40 },
      { x: 80, y: 8 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'post',
    wrPositionTrigger: { x: 80, y: 55 },
    cbResponse: [
      { x: 78, y: 50 },
      { x: 52, y: 18 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'corner',
    wrPositionTrigger: { x: 80, y: 55 },
    cbResponse: [
      { x: 82, y: 50 },
      { x: 93, y: 18 },
    ],
    responseDelay: 0,
  },
  // Default out (WR4 out) - match WR4
  {
    routeTrigger: 'out',
    wrPositionTrigger: { x: 80, y: 70 },
    cbResponse: [
      { x: 84, y: 65 },
      { x: 92, y: 65 },
    ],
    responseDelay: 0,
  },
  {
    // Drag: Shallow cross - match WR4 across
    routeTrigger: 'drag',
    wrPositionTrigger: { x: 80, y: 80 },
    cbResponse: [
      { x: 75, y: 65 },
      { x: 35, y: 48 },
    ],
    responseDelay: 0,
  },
  {
    // Hitch: 5-yard stop - match WR4
    routeTrigger: 'hitch',
    wrPositionTrigger: { x: 80, y: 70 },
    cbResponse: [
      { x: 78, y: 68 },
      { x: 76, y: 68 },
    ],
    responseDelay: 0,
  },
  {
    // Curl: 10-yard stop - match WR4, close on turn
    routeTrigger: 'curl',
    wrPositionTrigger: { x: 80, y: 55 },
    cbResponse: [
      { x: 78, y: 55 },
      { x: 76, y: 58 },
    ],
    responseDelay: 0,
  },
];

/* ── DB/LB2 Reactions (Cover 2 side) ──
 * Matches WR3 unless WR3 goes short out.
 * If WR3 short out, matches WR4 if WR4 goes short in (slant).
 * Otherwise zone drop.
 */
const cover6DBLB2Reactions: DBLB2Reaction[] = [
  // Match WR3 on all routes — trail closely
  {
    // WR3 slant (mirrored): (70,90)→(70,80)→(15,45)
    routeTrigger: 'slant',
    response: [{ x: 70, y: 68 }, { x: 68, y: 60 }, { x: 17, y: 43 }],
  },
  {
    // WR3 out (mirrored): (70,90)→(70,75)→(85,75)
    routeTrigger: 'out',
    response: [{ x: 70, y: 68 }, { x: 72, y: 73 }, { x: 87, y: 73 }],
  },
  {
    // WR3 deepOut (mirrored): (70,90)→(70,50)→(85,50)
    routeTrigger: 'deepOut',
    response: [{ x: 70, y: 65 }, { x: 71, y: 52 }, { x: 87, y: 48 }],
  },
  {
    // WR3 deepIn (mirrored): (70,90)→(70,50)→(30,50)
    routeTrigger: 'deepIn',
    response: [{ x: 70, y: 65 }, { x: 69, y: 52 }, { x: 33, y: 48 }],
  },
  {
    // WR3 comeback (mirrored): (70,90)→(70,30)→(75,45)
    routeTrigger: 'comeback',
    response: [{ x: 70, y: 65 }, { x: 71, y: 35 }, { x: 76, y: 47 }],
  },
  {
    // WR3 go (mirrored): (70,90)→(70,10)
    routeTrigger: 'go',
    response: [{ x: 70, y: 65 }, { x: 71, y: 40 }, { x: 71, y: 13 }],
  },
  {
    // WR3 post (mirrored): (70,90)→(70,60)→(40,20)
    routeTrigger: 'post',
    response: [{ x: 70, y: 65 }, { x: 69, y: 55 }, { x: 43, y: 23 }],
  },
  {
    // WR3 corner (mirrored): (70,90)→(70,60)→(85,20)
    routeTrigger: 'corner',
    response: [{ x: 70, y: 65 }, { x: 71, y: 55 }, { x: 87, y: 23 }],
  },
  {
    // WR3 drag (mirrored): shallow cross
    routeTrigger: 'drag',
    response: [{ x: 70, y: 68 }, { x: 65, y: 65 }, { x: 30, y: 58 }],
  },
  {
    // WR3 hitch (mirrored): 5-yard stop
    routeTrigger: 'hitch',
    response: [{ x: 70, y: 68 }, { x: 72, y: 73 }, { x: 74, y: 76 }],
  },
  {
    // WR3 curl (mirrored): 10-yard stop
    routeTrigger: 'curl',
    response: [{ x: 70, y: 65 }, { x: 71, y: 52 }, { x: 73, y: 58 }],
  },
  // ── Trips: DB/LB2 takes crosser from 3 WR (left) side ──
  // Strong-side WR drags/slants travel left→right and end near DB/LB2's zone
  {
    // Trips crosser (drag from 3 WR side): WR ends up around (70-80, 75-80)
    // DB/LB2 attacks downhill to meet the crosser as they enter his zone
    routeTrigger: 'drag_trips',
    response: [{ x: 70, y: 68 }, { x: 68, y: 72 }, { x: 70, y: 76 }, { x: 75, y: 78 }],
  },
  {
    // Trips crosser (slant from 3 WR side): WR ends up around (75-85, 38-46)
    // DB/LB2 closes deeper to match the slant as it enters his zone
    routeTrigger: 'slant_trips',
    response: [{ x: 70, y: 65 }, { x: 70, y: 55 }, { x: 75, y: 45 }, { x: 80, y: 42 }],
  },
  // WR3 short out → match WR4 slant (short in)
  // WR4 slant (mirrored, no offset): (80,85)→(80,75)→(25,40)
  {
    routeTrigger: 'slant_wr4',
    response: [{ x: 71, y: 65 }, { x: 75, y: 58 }, { x: 27, y: 38 }],
  },
  // WR3 short out, WR4 not short in → zone drop
  {
    routeTrigger: 'zone_drop',
    response: [{ x: 71, y: 65 }, { x: 72, y: 55 }, { x: 74, y: 45 }],
  },
  // RB-related triggers
  {
    routeTrigger: 'out_wr4',
    response: [{ x: 72, y: 65 }, { x: 78, y: 68 }, { x: 92, y: 68 }],
  },
  {
    routeTrigger: 'out_rb',
    response: [{ x: 71, y: 65 }, { x: 75, y: 72 }, { x: 85, y: 77 }],
  },
  {
    routeTrigger: 'swing_rb',
    response: [{ x: 71, y: 65 }, { x: 78, y: 75 }, { x: 90, y: 80 }],
  },
];

/* ── SS Reactions (Cover 2 deep half safety) ──
 * Responsible for deep help on the right side. Never lets anyone behind him.
 * Reads WR3 by default. If CB2 takes WR3 short out, matches WR4 on deep routes.
 */
const cover6SSReactions: SSReaction[] = [
  // WR3 routes — SS provides deep help, stays over the top
  {
    routeTrigger: 'slant',
    response: [{ x: 72, y: 30 }, { x: 70, y: 20 }],
  },
  {
    routeTrigger: 'out',
    response: [{ x: 72, y: 30 }, { x: 72, y: 18 }],
  },
  {
    // WR3 deepOut (mirrored): ends ~(85,50) — SS stays over top
    routeTrigger: 'deepOut',
    response: [{ x: 75, y: 28 }, { x: 82, y: 18 }],
  },
  {
    // WR3 deepIn (mirrored): ends ~(30,50) — SS stays deep, shades inside
    routeTrigger: 'deepIn',
    response: [{ x: 68, y: 28 }, { x: 55, y: 18 }],
  },
  {
    // WR3 comeback (mirrored): ends ~(75,45) — SS stays deep over top
    routeTrigger: 'comeback',
    response: [{ x: 74, y: 25 }, { x: 76, y: 15 }],
  },
  {
    // WR3 go (mirrored): ends ~(70,10) — SS runs with him deep
    routeTrigger: 'go',
    response: [{ x: 72, y: 25 }, { x: 72, y: 10 }, { x: 71, y: 5 }],
  },
  {
    // WR3 post (mirrored): ends ~(40,20) — SS stays in deep half, doesn't chase
    routeTrigger: 'post',
    response: [{ x: 72, y: 28 }, { x: 70, y: 18 }],
  },
  {
    // WR3 corner (mirrored): ends ~(85,20) — SS stays deep over top
    routeTrigger: 'corner',
    response: [{ x: 76, y: 28 }, { x: 85, y: 15 }, { x: 88, y: 10 }],
  },
  {
    // Drag: Short route - stay deep
    routeTrigger: 'drag',
    response: [{ x: 72, y: 30 }, { x: 70, y: 20 }],
  },
  {
    // Hitch: Short route - stay deep
    routeTrigger: 'hitch',
    response: [{ x: 72, y: 30 }, { x: 72, y: 18 }],
  },
  {
    // Curl: 10-yard stop - stay deep over top
    routeTrigger: 'curl',
    response: [{ x: 74, y: 25 }, { x: 76, y: 15 }],
  },
  // WR4 deep routes — when CB2 takes WR3 short out, SS matches WR4
  {
    // WR4 go (mirrored): ends ~(80,5)
    routeTrigger: 'go_wr4',
    response: [{ x: 76, y: 25 }, { x: 80, y: 12 }, { x: 80, y: 5 }],
  },
  {
    // WR4 post (mirrored): ends ~(50,15)
    routeTrigger: 'post_wr4',
    response: [{ x: 72, y: 28 }, { x: 60, y: 18 }, { x: 52, y: 12 }],
  },
  {
    // WR4 corner (mirrored): ends ~(95,15)
    routeTrigger: 'corner_wr4',
    response: [{ x: 78, y: 28 }, { x: 88, y: 18 }, { x: 93, y: 12 }],
  },
  {
    // WR4 deepOut (mirrored): ends ~(95,45)
    routeTrigger: 'deepOut_wr4',
    response: [{ x: 78, y: 28 }, { x: 88, y: 22 }, { x: 93, y: 18 }],
  },
  {
    // WR4 deepIn (mirrored): ends ~(40,45)
    routeTrigger: 'deepIn_wr4',
    response: [{ x: 68, y: 28 }, { x: 55, y: 20 }, { x: 42, y: 15 }],
  },
  {
    // WR4 comeback (mirrored): ends ~(85,40)
    routeTrigger: 'comeback_wr4',
    response: [{ x: 78, y: 25 }, { x: 82, y: 15 }, { x: 84, y: 12 }],
  },
  {
    // Trips Cover 6 single WR side — pure deep half zone, no match
    // Drop straight back to deep half, hold the middle of the right deep zone
    routeTrigger: 'zone_drop',
    response: [{ x: 75, y: 30 }, { x: 75, y: 18 }, { x: 75, y: 10 }],
  },
];

/* ── LB Reactions ──
 * Hook zone in the middle. Drops and picks up in-breaking routes.
 */
const cover6LBReactions: LBReaction[] = [
  {
    // WR1 slant: (20,85)→(20,75)→(75,40)
    // LB reads short, then breaks hard to match — ends right on WR1
    routeTrigger: 'slant_wr1',
    response: [
      { x: 50, y: 68 }, { x: 52, y: 55 }, { x: 62, y: 44 }, { x: 73, y: 38 },
    ],
  },
  {
    // WR2 slant (offset +10,+5): (30,90)→(30,80)→(85,45)
    // LB reads short, then breaks hard to match — ends right on WR2
    routeTrigger: 'slant_wr2',
    response: [
      { x: 50, y: 68 }, { x: 55, y: 55 }, { x: 68, y: 46 }, { x: 83, y: 43 },
    ],
  },
  {
    routeTrigger: 'slant_wr3',
    response: [
      { x: 50, y: 66 }, { x: 50, y: 62 }, { x: 56, y: 56 },
      { x: 62, y: 48 }, { x: 50, y: 40 }, { x: 38, y: 34 },
    ],
  },
  {
    routeTrigger: 'slant_wr4',
    response: [
      { x: 50, y: 66 }, { x: 50, y: 62 }, { x: 54, y: 56 },
      { x: 60, y: 48 }, { x: 52, y: 40 }, { x: 42, y: 32 },
    ],
  },
  {
    routeTrigger: 'zone_drop',
    response: [
      { x: 50, y: 66 }, { x: 50, y: 60 }, { x: 50, y: 52 }, { x: 50, y: 45 },
    ],
  },
  {
    // Follow WR1 on drag — WR goes (20,85) → (20,80) → (70,75)
    // LB reads short, breaks toward the shallow cross, trails tight
    routeTrigger: 'drag_wr1',
    response: [
      { x: 50, y: 68 }, { x: 52, y: 72 }, { x: 60, y: 74 }, { x: 68, y: 75 },
    ],
  },
  {
    // Follow WR2 on drag — WR2 offset: (30,90) → (30,85) → (80,80)
    // LB reads short, breaks toward the shallow cross, trails tight
    routeTrigger: 'drag_wr2',
    response: [
      { x: 50, y: 68 }, { x: 55, y: 74 }, { x: 65, y: 77 }, { x: 78, y: 79 },
    ],
  },
  {
    // Hammer Man on RB (#3) — RB stays in backfield or runs short check-release
    // LB shadows RB position from middle
    routeTrigger: 'man_rb',
    response: [
      { x: 53, y: 75 }, { x: 55, y: 80 }, { x: 55, y: 82 },
    ],
  },
  {
    // "Push" call — RB out fast to flat, LB works to cover #2 (WR2) with underneath leverage
    // LB shifts to strong side underneath, walling WR2
    routeTrigger: 'push_wr2',
    response: [
      { x: 48, y: 68 }, { x: 42, y: 62 }, { x: 35, y: 58 }, { x: 32, y: 55 },
    ],
  },
];

/**
 * Complete Cover 6 definition
 * Left side: Cover 4 Match (CB1, FS, DB/LB1)
 * Right side: Cover 2 (SS, CB2, DB/LB2)
 * Middle: LB hook zone
 */
export const cover6Definition: CoverageDefinition = {
  id: 'cover6',
  name: 'Cover 6',
  description: 'Cover 4 on the left, Cover 2 on the right',
  initialPosition: CB1_COVER6_START,
  reactionRules: cover6Reactions,
  dblb1InitialPosition: DBLB1_COVER6_START,
  dblb1ReactionRules: cover6DBLB1Reactions,
  fsInitialPosition: FS_COVER6_START,
  fsReactionRules: cover6FSReactions,
  cb2InitialPosition: CB2_COVER6_START,
  cb2ReactionRules: cover6CB2Reactions,
  dblb2InitialPosition: DBLB2_COVER6_START,
  dblb2ReactionRules: cover6DBLB2Reactions,
  ssInitialPosition: SS_COVER6_START,
  ssReactionRules: cover6SSReactions,
  lbInitialPosition: LB_COVER6_START,
  lbReactionRules: cover6LBReactions,
};

export function getCover6StartPosition(): Position {
  return { ...CB1_COVER6_START };
}

export function getCover6Definition(): CoverageDefinition {
  return { ...cover6Definition };
}

export {
  CB1_COVER6_START,
  DBLB1_COVER6_START,
  FS_COVER6_START,
  CB2_COVER6_START,
  DBLB2_COVER6_START,
  SS_COVER6_START,
  LB_COVER6_START,
  cover6Reactions,
  cover6DBLB1Reactions,
  cover6FSReactions,
  cover6CB2Reactions,
  cover6DBLB2Reactions,
  cover6SSReactions,
  cover6LBReactions,
};
